import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { 
  Building2, User, Mail, Image, ArrowRight, 
  CheckCircle, Loader2, Shield, Sparkles, Upload, X
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const content = {
  pt: {
    title: "Crie sua conta",
    titleFree: "Crie sua conta gratuita",
    subtitle: "Configure seu canal de compliance em minutos",
    step1: "Dados da Empresa",
    step2: "Dados do Administrador",
    step3: "Confirmação",
    companyName: "Nome da Empresa",
    companyNamePlaceholder: "Ex: Minha Empresa Ltda",
    cnpjEin: "CNPJ",
    cnpjEinPlaceholder: "00.000.000/0000-00",
    cnpjEinHelp: "Opcional - Será exibido na página da empresa",
    companyUrl: "URL do seu canal",
    logo: "Logo da Empresa",
    logoHelp: "Arraste uma imagem ou clique para selecionar",
    logoUploading: "Enviando...",
    removeLogo: "Remover",
    banner: "Banner da Empresa",
    bannerHelp: "Imagem de destaque (1920×890px)",
    bannerUploading: "Enviando banner...",
    removeBanner: "Remover",
    adminName: "Nome do Administrador",
    adminNamePlaceholder: "Seu nome completo",
    adminEmail: "Email do Administrador",
    adminEmailPlaceholder: "admin@empresa.com",
    adminPassword: "Senha de Acesso",
    adminPasswordPlaceholder: "Mínimo 6 caracteres",
    confirmPassword: "Confirmar Senha",
    confirmPasswordPlaceholder: "Digite a senha novamente",
    next: "Próximo",
    back: "Voltar",
    creating: "Criando empresa...",
    createCompany: "Criar minha conta",
    createCompanyFree: "Criar minha conta grátis",
    success: "Conta criada com sucesso!",
    error: "Erro ao criar empresa",
    slugExists: "Este identificador já está em uso",
    passwordMismatch: "As senhas não coincidem",
    termsAccept: "Li e aceito os",
    termsLink: "Termos de Uso",
    andThe: "e a",
    privacyLink: "Política de Privacidade",
    termsRequired: "Você deve aceitar os Termos de Uso e a Política de Privacidade",
    plan: "Plano",
    freePlan: "Gratuito",
    freeFeatures: ["1 usuário administrador", "3 registros por mês", "Canal de denúncias básico"],
    upgradeInfo: "Você pode fazer upgrade a qualquer momento",
    trialInfo: "7 dias grátis, depois",
    perMonth: "/mês",
    paidFeatures: ["7 dias de teste grátis", "Suporte via Email", "Recursos completos"]
  },
  en: {
    title: "Create your account",
    titleFree: "Create your free account",
    subtitle: "Set up your compliance channel in minutes",
    step1: "Company Details",
    step2: "Administrator Details",
    step3: "Confirmation",
    companyName: "Company Name",
    companyNamePlaceholder: "e.g. My Company Inc",
    cnpjEin: "EIN (Tax ID)",
    cnpjEinPlaceholder: "00-0000000",
    cnpjEinHelp: "Optional - Will be displayed on company page",
    companyUrl: "Your channel URL",
    logo: "Company Logo",
    logoHelp: "Drag an image or click to select",
    logoUploading: "Uploading...",
    removeLogo: "Remove",
    banner: "Company Banner",
    bannerHelp: "Featured image (1920×890px)",
    bannerUploading: "Uploading banner...",
    removeBanner: "Remove",
    adminName: "Administrator Name",
    adminNamePlaceholder: "Your full name",
    adminEmail: "Administrator Email",
    adminEmailPlaceholder: "admin@company.com",
    adminPassword: "Access Password",
    adminPasswordPlaceholder: "Minimum 6 characters",
    confirmPassword: "Confirm Password",
    confirmPasswordPlaceholder: "Enter password again",
    next: "Next",
    back: "Back",
    creating: "Creating company...",
    createCompany: "Create my account",
    createCompanyFree: "Create my free account",
    success: "Account created successfully!",
    error: "Error creating company",
    slugExists: "This identifier is already in use",
    passwordMismatch: "Passwords don't match",
    termsAccept: "I have read and accept the",
    termsLink: "Terms of Use",
    andThe: "and the",
    privacyLink: "Privacy Policy",
    termsRequired: "You must accept the Terms of Use and Privacy Policy",
    plan: "Plan",
    freePlan: "Free",
    freeFeatures: ["1 admin user", "3 reports per month", "Basic whistleblowing channel"],
    upgradeInfo: "You can upgrade anytime",
    trialInfo: "7 days free, then",
    perMonth: "/month",
    paidFeatures: ["7-day free trial", "Email support", "Full features"]
  }
};

// Plan details
const planDetails = {
  free: { name: { pt: "Gratuito", en: "Free" }, price: { pt: "R$ 0", en: "US$ 0" }, limit: 3 },
  essencial_brl: { name: { pt: "Essencial", en: "Essential" }, price: { pt: "R$ 89,00", en: "R$ 89.00" }, limit: 5 },
  essential_usd: { name: { pt: "Essential", en: "Essential" }, price: { pt: "US$ 29,00", en: "US$ 29.00" }, limit: 5 },
  profissional_brl: { name: { pt: "Profissional", en: "Professional" }, price: { pt: "R$ 199,00", en: "R$ 199.00" }, limit: 20 },
  professional_usd: { name: { pt: "Professional", en: "Professional" }, price: { pt: "US$ 49,00", en: "US$ 49.00" }, limit: 20 },
  empresarial_brl: { name: { pt: "Empresarial", en: "Enterprise" }, price: { pt: "R$ 499,00", en: "R$ 499.00" }, limit: -1 },
  enterprise_usd: { name: { pt: "Enterprise", en: "Enterprise" }, price: { pt: "US$ 129,00", en: "US$ 129.00" }, limit: -1 }
};

// Generate slug from company name
const generateSlug = (name) => {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Remove accents
    .replace(/[^a-z0-9\s-]/g, "") // Remove special chars
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/-+/g, "-") // Replace multiple hyphens
    .substring(0, 30) // Limit length
    .replace(/^-|-$/g, ""); // Remove leading/trailing hyphens
};

export default function OnboardingUnified() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { i18n } = useTranslation();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [verifyingSession, setVerifyingSession] = useState(true);
  
  const currentLang = i18n.language?.startsWith('en') ? 'en' : 'pt';
  const t = content[currentLang] || content.pt;
  
  // Get plan from URL params
  const planParam = searchParams.get('plan') || 'free';
  const sessionId = searchParams.get('session_id');
  const isFree = planParam === 'free';

  const [planInfo, setPlanInfo] = useState({
    id: planParam,
    name: planDetails[planParam]?.name[currentLang] || 'Free',
    price: planDetails[planParam]?.price[currentLang] || 'R$ 0',
    limit: planDetails[planParam]?.limit || 3
  });

  const [formData, setFormData] = useState({
    nome: "",
    slug: "",
    cnpj_ein: "",
    logo_url: "",
    banner_url: "",
    admin_nome: "",
    admin_email: "",
    admin_senha: "",
    confirm_senha: "",
    terms_accepted: false
  });

  // Verify Stripe session for paid plans
  useEffect(() => {
    const verifySession = async () => {
      if (isFree) {
        setVerifyingSession(false);
        return;
      }

      if (!sessionId) {
        toast.error(currentLang === 'pt' ? 'Sessão de pagamento não encontrada' : 'Payment session not found');
        navigate('/#pricing');
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/stripe/verify-session?session_id=${sessionId}`);
        const data = response.data;
        
        if (data.payment_status === 'paid' || data.status === 'complete') {
          setPlanInfo({
            id: data.plan_id || planParam,
            name: data.plan_name || planDetails[planParam]?.name[currentLang],
            price: planDetails[planParam]?.price[currentLang],
            limit: planDetails[planParam]?.limit || 5
          });
          setVerifyingSession(false);
        } else {
          toast.error(currentLang === 'pt' ? 'Pagamento não confirmado' : 'Payment not confirmed');
          navigate('/#pricing');
        }
      } catch (error) {
        console.error('Error verifying session:', error);
        toast.error(currentLang === 'pt' ? 'Erro ao verificar pagamento' : 'Error verifying payment');
        navigate('/#pricing');
      }
    };

    verifySession();
  }, [sessionId, isFree, navigate, currentLang, planParam]);

  // Auto-generate slug when company name changes
  useEffect(() => {
    if (formData.nome) {
      setFormData(prev => ({
        ...prev,
        slug: generateSlug(formData.nome)
      }));
    }
  }, [formData.nome]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error(currentLang === 'pt' ? 'Arquivo muito grande. Máximo 2MB.' : 'File too large. Max 2MB.');
      return;
    }

    setUploadingLogo(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API_URL}/upload/logo`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setFormData(prev => ({
        ...prev,
        logo_url: response.data.url
      }));
      toast.success(currentLang === 'pt' ? 'Logo enviado!' : 'Logo uploaded!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(currentLang === 'pt' ? 'Erro ao enviar logo' : 'Error uploading logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeLogo = () => {
    setFormData(prev => ({ ...prev, logo_url: "" }));
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 5MB for banners)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(currentLang === 'pt' ? 'Arquivo muito grande. Máximo 5MB.' : 'File too large. Max 5MB.');
      return;
    }

    setUploadingBanner(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API_URL}/upload/banner`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setFormData(prev => ({
        ...prev,
        banner_url: response.data.url
      }));
      toast.success(currentLang === 'pt' ? 'Banner enviado!' : 'Banner uploaded!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(currentLang === 'pt' ? 'Erro ao enviar banner' : 'Error uploading banner');
    } finally {
      setUploadingBanner(false);
    }
  };

  const removeBanner = () => {
    setFormData(prev => ({ ...prev, banner_url: "" }));
  };

  const validateStep1 = () => {
    if (!formData.nome) {
      toast.error(currentLang === 'pt' ? 'Digite o nome da empresa' : 'Enter company name');
      return false;
    }
    if (formData.nome.length < 3) {
      toast.error(currentLang === 'pt' ? 'Nome deve ter pelo menos 3 caracteres' : 'Name must have at least 3 characters');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.admin_nome || !formData.admin_email || !formData.admin_senha || !formData.confirm_senha) {
      toast.error(currentLang === 'pt' ? 'Preencha todos os campos' : 'Fill in all fields');
      return false;
    }
    if (formData.admin_senha !== formData.confirm_senha) {
      toast.error(t.passwordMismatch);
      return false;
    }
    if (formData.admin_senha.length < 6) {
      toast.error(currentLang === 'pt' ? 'Senha deve ter pelo menos 6 caracteres' : 'Password must have at least 6 characters');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const endpoint = isFree ? `${API_URL}/onboarding/free` : `${API_URL}/onboarding/empresa`;
      
      const payload = isFree ? {
        nome: formData.nome,
        slug: formData.slug,
        logo_url: formData.logo_url || null,
        banner_url: formData.banner_url || null,
        cnpj_ein: formData.cnpj_ein || null,
        admin_nome: formData.admin_nome,
        admin_email: formData.admin_email,
        admin_senha: formData.admin_senha,
        plan: 'free',
        terms_accepted: formData.terms_accepted,
        terms_version: "2026-01-23"
      } : {
        nome: formData.nome,
        slug: formData.slug,
        logo_url: formData.logo_url || null,
        banner_url: formData.banner_url || null,
        cnpj_ein: formData.cnpj_ein || null,
        admin_nome: formData.admin_nome,
        admin_email: formData.admin_email,
        admin_senha: formData.admin_senha,
        stripe_session_id: sessionId,
        plan_id: planInfo.id,
        plan_name: planInfo.name,
        terms_accepted: formData.terms_accepted,
        terms_version: "2026-01-23"
      };

      await axios.post(endpoint, payload);
      toast.success(t.success);
      
      setTimeout(() => {
        navigate(`/${formData.slug}/login`);
      }, 1500);
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.data?.detail?.includes('slug')) {
        toast.error(t.slugExists);
      } else {
        toast.error(error.response?.data?.detail || t.error);
      }
    } finally {
      setLoading(false);
    }
  };

  // Show loading while verifying paid session
  if (verifyingSession && !isFree) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-900">{currentLang === 'pt' ? 'Verificando pagamento...' : 'Verifying payment...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 font-ibm">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{isFree ? 'Criar Conta Gratuita - OmbudNow' : 'Criar Conta - OmbudNow'}</title>
        <meta name="description" content="Configure seu canal de compliance e ouvidoria em minutos. Crie sua conta e comece a receber denúncias com total sigilo e segurança." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900 font-chivo tracking-tight">OMBUDNOW</span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2 font-chivo">
            {isFree ? t.titleFree : t.title}
          </h1>
          <p className="text-slate-500">{t.subtitle}</p>
        </div>

        {/* Plan Badge */}
        <div className={`${isFree ? 'bg-emerald-50 border-emerald-200' : 'bg-blue-50 border-blue-200'} border rounded-xl p-4 mb-8`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 ${isFree ? 'bg-emerald-100' : 'bg-blue-100'} rounded-lg flex items-center justify-center`}>
                <Sparkles className={`w-5 h-5 ${isFree ? 'text-emerald-600' : 'text-blue-600'}`} />
              </div>
              <div>
                <p className={`${isFree ? 'text-emerald-700' : 'text-blue-700'} font-semibold`}>
                  {t.plan}: {planInfo.name}
                </p>
                <p className="text-slate-500 text-sm">
                  {isFree ? t.upgradeInfo : t.trialInfo + ' ' + planInfo.price + t.perMonth}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-slate-900 font-chivo">{planInfo.price}</p>
              {isFree && <p className="text-slate-500 text-xs">{currentLang === 'pt' ? 'para sempre' : 'forever'}</p>}
              {!isFree && <p className="text-slate-500 text-xs">{t.perMonth}</p>}
            </div>
          </div>
        </div>

        {/* Steps Indicator */}
        <div className="flex items-center justify-center gap-4 mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all
                ${step >= s ? 'bg-slate-900 text-slate-900' : 'bg-slate-200 text-slate-500'}`}>
                {step > s ? <CheckCircle className="w-5 h-5" /> : s}
              </div>
              {s < 3 && (
                <div className={`w-16 h-0.5 mx-2 transition-all ${step > s ? 'bg-slate-900' : 'bg-slate-200'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8">
          {/* Step 1: Company Details */}
          {step === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2 font-chivo">
                <Building2 className="w-5 h-5 text-blue-600" />
                {t.step1}
              </h2>

              <div className="space-y-4">
                <div>
                  <Label className="text-slate-700 mb-2 block">{t.companyName} *</Label>
                  <Input
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    placeholder={t.companyNamePlaceholder}
                    className="border-slate-200"
                    data-testid="input-company-name"
                  />
                </div>

                {/* Auto-generated URL preview */}
                {formData.slug && (
                  <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                    <Label className="text-slate-500 text-sm mb-1 block">{t.companyUrl}</Label>
                    <p className="text-slate-900 font-mono text-sm">
                      ombudnow.com/<span className="text-blue-600">{formData.slug}</span>
                    </p>
                  </div>
                )}

                {/* CNPJ/EIN Field */}
                <div>
                  <Label className="text-slate-700 mb-2 block">{t.cnpjEin}</Label>
                  <Input
                    name="cnpj_ein"
                    value={formData.cnpj_ein}
                    onChange={handleChange}
                    placeholder={t.cnpjEinPlaceholder}
                    className="border-slate-200"
                    data-testid="input-cnpj-ein"
                  />
                  <p className="text-slate-500 text-xs mt-1">{t.cnpjEinHelp}</p>
                </div>

                {/* Logo Upload */}
                <div>
                  <Label className="text-slate-700 mb-2 block">{t.logo}</Label>
                  {formData.logo_url ? (
                    <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <img 
                        src={formData.logo_url} 
                        alt="Logo" 
                        className="w-16 h-16 object-contain rounded-lg bg-white/10"
                      />
                      <div className="flex-1">
                        <p className="text-slate-700 text-sm truncate">{formData.logo_url.split('/').pop()}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        onClick={removeLogo}
                      >
                        <X className="w-4 h-4 mr-1" />
                        {t.removeLogo}
                      </Button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-[#f5a623]/50 hover:bg-slate-50 transition-all">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                        disabled={uploadingLogo}
                      />
                      {uploadingLogo ? (
                        <div className="flex items-center gap-2 text-slate-500">
                          <Loader2 className="w-6 h-6 animate-spin" />
                          <span>{t.logoUploading}</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2 text-slate-500">
                          <Upload className="w-8 h-8" />
                          <span className="text-sm">{t.logoHelp}</span>
                        </div>
                      )}
                    </label>
                  )}
                </div>

                {/* Banner Upload */}
                <div>
                  <Label className="text-slate-700 mb-2 block">{t.banner}</Label>
                  {formData.banner_url ? (
                    <div className="relative p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <img 
                        src={formData.banner_url} 
                        alt="Banner" 
                        className="w-full h-24 object-cover rounded-lg"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute top-2 right-2 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        onClick={removeBanner}
                      >
                        <X className="w-4 h-4 mr-1" />
                        {t.removeBanner}
                      </Button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-[#f5a623]/50 hover:bg-slate-50 transition-all">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleBannerUpload}
                        className="hidden"
                        disabled={uploadingBanner}
                      />
                      {uploadingBanner ? (
                        <div className="flex items-center gap-2 text-slate-500">
                          <Loader2 className="w-6 h-6 animate-spin" />
                          <span>{t.bannerUploading}</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-1 text-slate-500">
                          <Image className="w-6 h-6" />
                          <span className="text-sm">{t.bannerHelp}</span>
                        </div>
                      )}
                    </label>
                  )}
                </div>
              </div>

              <Button
                className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold"
                onClick={() => validateStep1() && setStep(2)}
                data-testid="btn-next-step1"
              >
                {t.next}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* Step 2: Administrator Details */}
          {step === 2 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                {t.step2}
              </h2>

              <div className="space-y-4">
                <div>
                  <Label className="text-slate-700 mb-2 block">{t.adminName} *</Label>
                  <Input
                    name="admin_nome"
                    value={formData.admin_nome}
                    onChange={handleChange}
                    placeholder={t.adminNamePlaceholder}
                    className="border-slate-200 text-slate-900 placeholder:text-slate-400"
                    data-testid="input-admin-name"
                  />
                </div>

                <div>
                  <Label className="text-slate-700 mb-2 block">{t.adminEmail} *</Label>
                  <Input
                    name="admin_email"
                    type="email"
                    value={formData.admin_email}
                    onChange={handleChange}
                    placeholder={t.adminEmailPlaceholder}
                    className="border-slate-200 text-slate-900 placeholder:text-slate-400"
                    data-testid="input-admin-email"
                  />
                </div>

                <div>
                  <Label className="text-slate-700 mb-2 block">{t.adminPassword} *</Label>
                  <Input
                    name="admin_senha"
                    type="password"
                    value={formData.admin_senha}
                    onChange={handleChange}
                    placeholder={t.adminPasswordPlaceholder}
                    className="border-slate-200 text-slate-900 placeholder:text-slate-400"
                    data-testid="input-admin-password"
                  />
                </div>

                <div>
                  <Label className="text-slate-700 mb-2 block">{t.confirmPassword} *</Label>
                  <Input
                    name="confirm_senha"
                    type="password"
                    value={formData.confirm_senha}
                    onChange={handleChange}
                    placeholder={t.confirmPasswordPlaceholder}
                    className="border-slate-200 text-slate-900 placeholder:text-slate-400"
                    data-testid="input-confirm-password"
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <Button
                  variant="outline"
                  className="flex-1 border-slate-200 text-slate-900 hover:bg-white/10"
                  onClick={() => setStep(1)}
                  data-testid="btn-back-step2"
                >
                  {t.back}
                </Button>
                <Button
                  className="flex-1 bg-slate-900 hover:bg-slate-800 text-white font-semibold"
                  onClick={() => validateStep2() && setStep(3)}
                  data-testid="btn-next-step2"
                >
                  {t.next}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Confirmation */}
          {step === 3 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-blue-600" />
                {t.step3}
              </h2>

              {/* Summary */}
              <div className="bg-slate-50 rounded-xl p-6 space-y-4">
                <div className="flex items-center gap-4 pb-4 border-b border-slate-200">
                  <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center overflow-hidden">
                    {formData.logo_url ? (
                      <img src={formData.logo_url} alt="Logo" className="w-full h-full object-contain" />
                    ) : (
                      <Building2 className="w-6 h-6 text-slate-500" />
                    )}
                  </div>
                  <div>
                    <p className="text-slate-900 font-semibold">{formData.nome}</p>
                    <p className="text-slate-500 text-sm">ombudnow.com/{formData.slug}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-slate-700">
                    <User className="w-4 h-4 text-slate-500" />
                    <span>{formData.admin_nome}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-700">
                    <Mail className="w-4 h-4 text-slate-500" />
                    <span>{formData.admin_email}</span>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-200">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">{t.plan}</span>
                    <span className={`${isFree ? 'text-emerald-400' : 'text-blue-600'} font-semibold`}>
                      {planInfo.name}
                    </span>
                  </div>
                  <ul className="mt-3 space-y-2">
                    {(isFree ? t.freeFeatures : t.paidFeatures).map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-500 text-sm">
                        <CheckCircle className={`w-4 h-4 ${isFree ? 'text-emerald-500' : 'text-blue-600'}`} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* LGPD Terms Acceptance */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <label className="flex items-start gap-3 cursor-pointer" data-testid="terms-checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.terms_accepted}
                    onChange={(e) => setFormData(prev => ({ ...prev, terms_accepted: e.target.checked }))}
                    className="mt-1 w-4 h-4 rounded border-amber-300 text-slate-900 focus:ring-slate-900"
                    data-testid="terms-checkbox"
                  />
                  <span className="text-slate-700 text-sm leading-relaxed">
                    {t.termsAccept}{" "}
                    <a
                      href={currentLang === 'en' ? '/en/terms-of-use' : '/pt/termos-de-uso'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline hover:text-blue-800 font-medium"
                    >
                      {t.termsLink}
                    </a>{" "}
                    {t.andThe}{" "}
                    <a
                      href={currentLang === 'en' ? '/en/privacy-policy' : '/pt/politica-de-privacidade'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline hover:text-blue-800 font-medium"
                    >
                      {t.privacyLink}
                    </a>
                  </span>
                </label>
              </div>

              <div className="flex gap-4">
                <Button
                  variant="outline"
                  className="flex-1 border-slate-200 text-slate-900 hover:bg-white/10"
                  onClick={() => setStep(2)}
                  disabled={loading}
                >
                  {t.back}
                </Button>
                <Button
                  className="flex-1 bg-slate-900 hover:bg-slate-800 text-white font-semibold disabled:opacity-50"
                  onClick={() => {
                    if (!formData.terms_accepted) {
                      toast.error(t.termsRequired);
                      return;
                    }
                    handleSubmit();
                  }}
                  disabled={loading || !formData.terms_accepted}
                  data-testid="btn-create-company"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t.creating}
                    </>
                  ) : (
                    <>
                      {isFree ? t.createCompanyFree : t.createCompany}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
